<template>
  <div class="eventmap">
    <div ref="container" style="position: absolute; inset: 0" :class="{ 'cursor-not-allowed': lockSelected }">
      <!-- Zoom Instructions -->
      <EventmapZoomInstructions v-if="showZoomInstructions" ref="zoomInstructionsRef" />

      <div class="zoom-indicator" style="display: none;">
        Zoom: {{ zoomFactor.toFixed(2) }}x
      </div>

      <!-- SVG Canvas -->
      <svg
        v-if="eventmap"
        :viewBox="viewBox.join(' ')"
        ref="svg"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
      >
        <!-- Background Image -->
        <image x="0" y="0" :width="eventmap.width" :href="eventmap.background_url" />

        <!-- Grid Overlay -->
        <EventmapGrid
          v-if="showGrid"
          :eventmapWidth="eventmap.width"
          :eventmapDepth="eventmap.depth"
          :roundingFactor="eventmap.rounding_factor"
        />

        <!-- Render all eventmap locations -->
        <FeEventmapStand
          v-for="location in eventmap.locations"
          :key="location.id"
          :location="location"
          @click="isPanning ? null : locationClicked(location)"
          :defaultPalette="eventmap.palette"
          v-show="location.id !== selectedLocation?.id"
        />

        <!-- Render the selected location stand -->
        <FeEventmapStand
          v-if="selectedLocation"
          :key="selectedLocation.id"
          ref="selectedLocationEl"
          :location="selectedLocation"
          overrideStrokeColour="#000000"
          overrideFillColour="#1a24ff"
          overrideTextColour="#ffffff"
          :defaultPalette="eventmap.palette"
          :draggable="lockSelected"
          :isDragging="lockSelected && isDragging"
          :className="flashSelectedLocation ? 'fe-flash-selected' : ''"
          :textClassName="flashSelectedLocation ? 'fe-selected-text' : ''"
        />

        <!-- Location Handles -->
        <EventmapLocationHandles
          v-if="lockSelected && selectedLocation && selectedLocation.palette_key !== 'kiosk'"
          :selectedLocation="selectedLocation"
          @setPolyPointMoving="setPolyPointMoving"
          ref="locationHandles"
        />

        <!-- Paths Layer -->
        <EventmapPathsLayer
          :eventmap="eventmap"
          :showPaths="showPaths"
          :selectedPath="selectedPath"
          :lockSelected="lockSelected"
          :overridePathColor="overridePathColor"
          @pathClicked="pathClicked"
          ref="pathsLayer"
        />

        <!-- Heatmap Layer -->
        <EventmapHeatmapLayer :heatMap="heatMap" />

        <!-- Active Path -->
        <EventmapRoute
          v-if="activePath && activePath.length"
          :activePath="activePath"
          :zoomFactor="zoomFactor"
        />
      </svg>

      <!-- Coordinates Display -->
      <div
        v-if="showCoords && svgX !== null && svgY !== null"
        class="eventmap__coords"
      >
        <i class="fas fa-compass me-1"></i>
        {{ (svgX / eventmap.rounding_factor).toFixed(2) }},
        {{ (svgY / eventmap.rounding_factor).toFixed(2) }}
      </div>
    </div>
  </div>
</template>

<script setup>
// Import necessary modules and components
import {
  ref, onMounted, nextTick, computed,
} from 'vue';
import { useElementSize } from '@vueuse/core';
import FeEventmapStand from '~/Components/Common/Eventmap/FeEventmapStand.vue';
import EventmapGrid from '~/Components/Common/Eventmap/EventmapGrid.vue';
import EventmapRoute from '~/Components/Common/Eventmap/EventmapRoute.vue';
import useEventmap from '~/Components/Common/Eventmap/Composables/useEventmap';
import EventmapZoomInstructions from '~/Components/Common/Eventmap/EventmapZoomInstructions.vue';
import EventmapLocationHandles from '~/Components/Common/Eventmap/EventmapLocationHandles.vue';
import EventmapPathsLayer from '~/Components/Common/Eventmap/EventmapPathsLayer.vue';
import EventmapHeatmapLayer from '~/Components/Common/Eventmap/EventmapHeatmapLayer.vue';

// Define component props
const props = defineProps({
  eventmap: Object, // Eventmap data object
  showGrid: Boolean, // Toggle grid visibility
  showPaths: Boolean, // Toggle paths visibility
  showCoords: Boolean, // Toggle coordinate display
  showZoomInstructions: { type: Boolean, default: false }, // Show zoom instructions
  width: Number,
  height: Number,
  lockSelected: Boolean, // Lock selected location or path
  activePath: { type: Array, default: () => [] }, // Active path data
  selectedLocation: Object, // Currently selected location
  flashSelectedLocation: Boolean, // Flash effect for selected location
  overridePathColor: String, // Override color for paths
  selectedPath: Object, // Currently selected path
  heatMap: { type: Array, default: () => [] }, // Heatmap data
});

// Define emitted events
const emit = defineEmits([
  'svgReady', // Emitted when SVG is ready
  'svgClicked', // Emitted when SVG is clicked
  'contentMenuOpened', // Emitted when context menu is opened
  'hover', // Emitted on hover
  'locationClicked', // Emitted when a location is clicked
  'locationDblClicked', // Emitted when a location is double-clicked
  'locationMove', // Emitted during location move
  'locationMoved', // Emitted after location move
  'locationResize', // Emitted during location resize
  'locationResized', // Emitted after location resize
  'pathClicked', // Emitted when a path is clicked
  'pathDblClicked', // Emitted when a path is double-clicked
  'pathNodeMove', // Emitted during path node move
  'pathNodeMoved', // Emitted after path node move
  'polyPointMove', // Emitted during polygon point move
  'polyPointMoved', // Emitted after polygon point move
]);

// Refs for DOM elements
const container = ref(null);
const svg = ref(null);
const zoomInstructionsRef = ref(null);
const selectedLocationEl = ref(null);

// Child component refs
const locationHandles = ref(null);
const pathsLayer = ref(null);

// State variables
const isPanning = ref(false); // Tracks panning state
const isDragging = ref(false); // Tracks dragging state
const svgX = ref(null); // Tracks SVG X coordinate
const svgY = ref(null); // Tracks SVG Y coordinate
const isPolyPointMoving = ref(false); // Tracks polygon point movement

const isPinching = ref(false); // Tracks pinch gesture state
const isResizing = ref(false); // Tracks resizing direction
const isPathNodeMoving = ref(false); // Tracks path node movement

// Get container dimensions
const { width: containerWidth, height: containerHeight } = useElementSize(container);

// Computed properties for handles and selected path
const topHandle = computed(() => (locationHandles.value ? locationHandles.value.topHandle : null));
const rightHandle = computed(() => (locationHandles.value ? locationHandles.value.rightHandle : null));
const bottomHandle = computed(() => (locationHandles.value ? locationHandles.value.bottomHandle : null));
const leftHandle = computed(() => (locationHandles.value ? locationHandles.value.leftHandle : null));

const pathHandle1 = computed(() => (pathsLayer.value ? pathsLayer.value.pathHandle1 : null));
const pathHandle2 = computed(() => (pathsLayer.value ? pathsLayer.value.pathHandle2 : null));
const selectedPathEl = computed(() => (pathsLayer.value ? pathsLayer.value.selectedPathEl : null));

const zoomInstructions = computed(() => (zoomInstructionsRef.value ? zoomInstructionsRef.value.zoomInstructions : null));

// Initialize eventmap composable
const {
  viewBox,
  zoomFactor,
  resetViewBox,
  zoom,
  zoomToLocation,
  zoomToExtents,
  initializeEventListeners,
} = useEventmap({
  props,
  emit,
  refs: {
    svg,
    zoomInstructions,
    selectedLocationEl,
    selectedPathEl,
    topHandle,
    rightHandle,
    bottomHandle,
    leftHandle,
    pathHandle1,
    pathHandle2,
    isPanning,
    isDragging,
    isPolyPointMoving,
    svgX,
    svgY,
    isPinching,
    isResizing,
    isPathNodeMoving,
  },
  containerWidth,
  containerHeight,
});

// Set polyline point moving state
const setPolyPointMoving = (i) => {
  isPolyPointMoving.value = i;
};

// Handle location click event
const locationClicked = (location) => {
  if (props.lockSelected || (props.selectedLocation && props.selectedLocation.id === location.id)) return;
  emit('locationClicked', location);
};

// Handle path click event
const pathClicked = (path) => {
  if (props.lockSelected || (props.selectedPath && props.selectedPath.id === path.id)) return;
  emit('pathClicked', path);
};

// Lifecycle hook: onMounted
onMounted(() => {
  resetViewBox(false);
  emit('svgReady');
  initializeEventListeners();
});

// Expose download functionality
async function download() {
  await nextTick();
  const blob = new Blob([svg.value.outerHTML], { type: 'text/svg' });
  const filename = 'eventmap.svg';
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const elem = document.createElement('a');
    elem.href = URL.createObjectURL(blob);
    elem.download = filename;
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
  }
}

// Expose methods to parent components
defineExpose({
  download,
  zoomToLocation,
  resetViewBox,
  zoom,
  zoomToExtents,
  svg,
});
</script>

<style>
.eventmap {
  position: relative;
  background-color: #F8FAFC;
  width:100%;
  height: 100%;
  max-width:100%;
}
.eventmap__coords {
  position: absolute;
  right: 0;
  bottom: 0;
  margin: 1rem;
  padding: 0.5rem;
  min-width: 10rem;
  text-align: center;
  font-size: 12px;
  z-index: 100;
  color: rgb(15 23 42 / 1);
  background-color: #fff;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border-radius: 0.25rem;
}
.fe-flash-selected {
  fill: var(--event-map-selected-flash-from);
  /* @keyframes duration | easing-function | delay | iteration-count | direction | fill-mode | play-state | name */
  animation: feFlash 0.8s ease-in 2s infinite alternate-reverse;
}
.fe-selected-text {
  fill: var(--system-white);
}
@keyframes feFlash {
  to {
    fill: var(--event-map-selected-flash-to);
  }
}
</style>
